import { Head, Link } from "@inertiajs/react"
import { Inbox, FileText, Signpost, Users, Palette, Sparkles, ExternalLink, ChevronRight, Filter } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"

type FunnelData = {
  submitted: number
  qualified: number
  booked: number
  qualified_rate: number
  booked_rate: number
}

type Stats = {
  applications: { total: number; new_this_week: number; qualified: number; booked: number }
  funnel: { all: FunnelData; last_30: FunnelData }
  posts: { total: number; published: number; drafts: number }
  redirects: { total: number }
  users: { total: number }
}

export default function AdminDashboard({ stats }: { stats: Stats }) {
  return (
    <>
      <Head title="Admin">
        <meta name="description" content="FullCast admin dashboard." />
        <meta property="og:title" content="Admin" />
        <meta property="og:description" content="FullCast admin dashboard." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Dashboard</h1>
          <p className="mt-1">Everything you manage on the FullCast site, in one place.</p>
        </div>

        <FunnelCard funnel={stats.funnel} />

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <SectionCard
            href="/admin/applications"
            icon={Inbox}
            title="Applications"
            primary={stats.applications.total}
            primaryLabel={stats.applications.total === 1 ? "application" : "applications"}
            facts={[
              `${stats.applications.new_this_week} new this week`,
              `${stats.applications.qualified} qualified`,
              `${stats.applications.booked} booked`,
            ]}
          />
          <SectionCard
            href="/admin/posts"
            icon={FileText}
            title="Blog"
            primary={stats.posts.total}
            primaryLabel={stats.posts.total === 1 ? "post" : "posts"}
            facts={[`${stats.posts.published} published`, `${stats.posts.drafts} drafts`]}
          />
          <SectionCard
            href="/admin/redirects"
            icon={Signpost}
            title="Redirects"
            primary={stats.redirects.total}
            primaryLabel={stats.redirects.total === 1 ? "redirect" : "redirects"}
            facts={["Manage or import a CSV"]}
          />
          <SectionCard
            href="/admin/users"
            icon={Users}
            title="Users"
            primary={stats.users.total}
            primaryLabel={stats.users.total === 1 ? "user" : "users"}
            facts={["Admin accounts"]}
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <LinkCard href="/admin/design-system" icon={Palette} title="Design system" subtitle="Tokens, components, and styles" />
          <LinkCard href="/admin/skills" icon={Sparkles} title="Skills" subtitle="Claude Code skills available in this repo" />
          <LinkCard href="/" icon={ExternalLink} title="View live site" subtitle="Open the public site" external />
        </div>
      </AdminShell>
    </>
  )
}

function SectionCard({
  href,
  icon: Icon,
  title,
  primary,
  primaryLabel,
  facts,
}: {
  href: string
  icon: React.ComponentType<{ className?: string }>
  title: string
  primary: number
  primaryLabel: string
  facts: string[]
}) {
  return (
    <Link
      href={href}
      className="group block rounded-lg border border-hairline bg-page p-5 no-underline transition-colors hover:bg-surface"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-ink-muted">
          <Icon className="h-4 w-4" />
          <span className="text-sm font-medium text-ink-display">{title}</span>
        </span>
        <ChevronRight className="h-4 w-4 text-ink-muted transition-transform group-hover:translate-x-0.5" />
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-3xl font-semibold text-ink-display">{primary}</span>
        <span className="text-sm text-ink-muted">{primaryLabel}</span>
      </div>
      <p className="mt-2 text-sm text-ink-muted">{facts.join(" · ")}</p>
    </Link>
  )
}

function FunnelCard({ funnel }: { funnel: Stats["funnel"] }) {
  return (
    <div className="mt-6 rounded-lg border border-hairline bg-page p-5">
      <span className="inline-flex items-center gap-2 text-ink-muted">
        <Filter className="h-4 w-4" />
        <span className="text-sm font-medium text-ink-display">Lead funnel</span>
      </span>
      <div className="mt-4 grid gap-8 sm:grid-cols-2">
        <FunnelColumn label="Last 30 days" data={funnel.last_30} />
        <FunnelColumn label="All time" data={funnel.all} />
      </div>
      <p className="mt-4 text-xs text-ink-muted">
        Visitors → page → CTA clicks live in Fathom; this is submitted → qualified → booked.
      </p>
    </div>
  )
}

function FunnelColumn({ label, data }: { label: string; data: FunnelData }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</p>
      <div className="mt-3 space-y-3">
        <FunnelRow step="Submitted" count={data.submitted} />
        <FunnelRow step="Qualified" count={data.qualified} rate={data.qualified_rate} of="submitted" />
        <FunnelRow step="Booked" count={data.booked} rate={data.booked_rate} of="qualified" />
      </div>
    </div>
  )
}

function FunnelRow({
  step,
  count,
  rate,
  of,
}: {
  step: string
  count: number
  rate?: number
  of?: string
}) {
  return (
    <div className="flex items-baseline justify-between border-b border-hairline pb-2 last:border-0">
      <span className="text-sm text-ink-body">{step}</span>
      <span className="flex items-baseline gap-2">
        <span className="font-display text-xl font-semibold text-ink-display">{count}</span>
        {rate !== undefined && (
          <span className="text-xs text-ink-muted">
            {rate}% of {of}
          </span>
        )}
      </span>
    </div>
  )
}

function LinkCard({
  href,
  icon: Icon,
  title,
  subtitle,
  external,
}: {
  href: string
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle: string
  external?: boolean
}) {
  const inner = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface">
        <Icon className="h-4 w-4 text-ink-muted" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-ink-display">{title}</span>
        <span className="block text-sm text-ink-muted">{subtitle}</span>
      </span>
      <ChevronRight className="h-4 w-4 text-ink-muted" />
    </>
  )
  const className =
    "flex items-center gap-3 rounded-lg border border-hairline bg-page p-4 no-underline transition-colors hover:bg-surface"

  return external ? (
    <a href={href} className={className}>{inner}</a>
  ) : (
    <Link href={href} className={className}>{inner}</Link>
  )
}
